import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchStatuses } from '@/services/meta.service'
import type {
  DocTypeStatusSpec,
  StatusBadgeVariant,
  StatusOption,
  StatusRegistryPayload,
} from '@/types/statusRegistry.types'

export type { DocTypeStatusSpec, StatusOption, StatusRegistryPayload }

/**
 * Single client-side source of status options and transitions.
 *
 * The vocabulary comes exclusively from `status_registry.py` via
 * `GET /api/v1/meta/statuses`; there is intentionally no fallback list, so a stale
 * copy can never drift from the server. If the registry cannot load, status-change
 * menus stay empty and labels are not resolved.
 */
export const useStatusRegistryStore = defineStore('statusRegistry', () => {
  const loaded = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const documents = ref<DocTypeStatusSpec[]>([])
  const derived = ref<StatusRegistryPayload['derived']>([])

  const docMap = computed(() => new Map(documents.value.map((doc) => [doc.doc_type, doc])))
  const docTypes = computed(() => documents.value.map((doc) => doc.doc_type))

  let inflight: Promise<void> | null = null

  async function ensureLoaded(force = false): Promise<void> {
    if (inflight) return inflight
    if (loaded.value && !force) return

    loading.value = true
    error.value = null
    inflight = (async () => {
      const payload = await fetchStatuses()
      if (payload) {
        documents.value = payload.documents ?? []
        derived.value = payload.derived ?? []
        loaded.value = true
      } else {
        error.value = 'status_registry_unavailable'
      }
    })()

    try {
      await inflight
    } finally {
      inflight = null
      loading.value = false
    }
  }

  function reset(): void {
    loaded.value = false
    loading.value = false
    error.value = null
    documents.value = []
    derived.value = []
  }

  function spec(docType: string): DocTypeStatusSpec | undefined {
    return docMap.value.get(docType)
  }

  function businessOptions(docType: string): StatusOption[] {
    return spec(docType)?.business?.options ?? []
  }

  function optionByCode(docType: string, code: number | null | undefined): StatusOption | undefined {
    if (code == null) return undefined
    return businessOptions(docType).find((option) => option.code === Number(code))
  }

  function optionBySlug(docType: string, slug: string | null | undefined): StatusOption | undefined {
    if (!slug) return undefined
    return businessOptions(docType).find((option) => option.slug === slug)
  }

  /** Resolve a numeric code or slug; legacy aliases map to their canonical option. */
  function resolveOption(
    docType: string,
    value: number | string | null | undefined,
  ): StatusOption | undefined {
    if (value == null || value === '') return undefined
    const raw = String(value)
    const option = /^\d+$/.test(raw)
      ? optionByCode(docType, Number(raw))
      : optionBySlug(docType, raw)
    if (!option?.legacy_alias) return option
    return optionBySlug(docType, option.slug) ?? option
  }

  /** Options a user may move to from the current code/slug (server `allowed_from`).
   *
   * The workflow bar owns cancellation, so the `on_cancel_code` option is excluded by
   * default; pass `{ includeCancel: true }` to show it.
   */
  function targets(
    docType: string,
    current: number | string | null | undefined,
    options?: { includeCancel?: boolean },
  ): StatusOption[] {
    const currentOption = resolveOption(docType, current)
    if (currentOption?.code == null) return []
    const from = currentOption.code
    const cancelCode = spec(docType)?.business?.on_cancel_code ?? null
    return businessOptions(docType).filter(
      (option) =>
        option.code != null &&
        !option.legacy_alias &&
        option.allowed_from.includes(from) &&
        (options?.includeCancel || option.code !== cancelCode),
    )
  }

  /** Same as `targets`, accepting a slug (projects and contracts use slugs). */
  function targetsBySlug(
    docType: string,
    slug: string | null | undefined,
    options?: { includeCancel?: boolean },
  ): StatusOption[] {
    return targets(docType, slug, options)
  }

  function labelKey(
    docType: string,
    value: number | string | null | undefined,
  ): string | undefined {
    return resolveOption(docType, value)?.label_key
  }

  function variant(
    docType: string,
    value: number | string | null | undefined,
  ): StatusBadgeVariant | undefined {
    return resolveOption(docType, value)?.variant
  }

  function isTerminal(docType: string, value: number | string | null | undefined): boolean {
    const option = resolveOption(docType, value)
    if (!option) return false
    return spec(docType)?.business?.terminal_slugs.includes(option.slug) ?? false
  }

  return {
    loaded,
    loading,
    error,
    documents,
    derived,
    docTypes,
    ensureLoaded,
    reset,
    spec,
    businessOptions,
    optionByCode,
    optionBySlug,
    resolveOption,
    targets,
    targetsBySlug,
    labelKey,
    variant,
    isTerminal,
  }
})
